'use client';

import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { z } from 'zod';
import { FormProvider, useForm } from 'react-hook-form';
import { message, Skeleton } from 'antd';

import { useEffect, useRef } from 'react';
import { SellerProductFullDetails } from '@/components/Products/ProductDetails/types';
import { useLazyGetCategoryAttributesQuery } from '../../../../../../store/services/lookups';
import {
  useAddSellerProductByAdminMutation,
  useEditSellerProductByAdminMutation,
  useGetSellerProductFullDetailsQuery,
} from '../../../../../../store/services/products';


import { useAddProductSchema, useAddProductResolver } from '../_hooks/useAddProductSchema';
import { buildAddProductPayload } from '../_hooks/buildAddProductPayload';
import { mapProductToFormValues, type CategoryAttribute } from '../_hooks/mapProductToFormValues';

import ProductInformationForm from './ProductInformationForm';
import PricingTradeForm from './PricingTradeForm';
import SpecificationsForm from './SpecificationsForm';
import SkuInventoryForm from './SkuInventoryForm';
import CustomizationForm from './CustomizationForm';
import { getFirstErrorMsg } from '../../../../../../_utils/formHelpers';
import { ADD_PRODUCT_DEFAULT_VALUES, ADD_PRODUCT_FIELD_MAPPING } from '../_constants/addProductForm';
import { AddProductLookupsProvider } from '../_context/AddProductLookupsContext';
import ScrollToError from '@/components/atoms/ScrollToError';

export type AddProductSchema = z.infer<ReturnType<typeof useAddProductSchema>>;


const AddProductContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sellerId } = useParams();
  const [searchParams] = useSearchParams();
  const editProductId = searchParams.get('productId');
  const productId = editProductId;
  const exchangeRate = 1;

  const addProductResolver = useAddProductResolver();

  const [createProductApi, { isLoading: isSubmittingCreate }] = useAddSellerProductByAdminMutation();
  const [updateProductApi, { isLoading: isSubmittingUpdate }] = useEditSellerProductByAdminMutation();

  const { data: productDetailsData, isLoading: isLoadingFullDetails } = useGetSellerProductFullDetailsQuery(
    productId as string,
    { skip: !productId }
  );

  const [getCategoryAttributes] = useLazyGetCategoryAttributesQuery();

  const isSubmitting = isSubmittingCreate || isSubmittingUpdate;

  const handleBackendErrors = (err: any, setError: any, fieldMapping: any) => {
    const errorData = err?.data?.details || err?.data || err;
    if (errorData?.errors) {
      Object.keys(errorData.errors).forEach((backendKey) => {
        const frontendKey = fieldMapping[backendKey] || backendKey;
        setError(frontendKey, { type: 'server', message: errorData.errors[backendKey] });
      });
    } else {
      message.error(errorData?.message || 'Something went wrong');
    }
  };

  const methods = useForm<AddProductSchema>({
    resolver: addProductResolver,
    mode: 'all',
    shouldFocusError: false,
    defaultValues: ADD_PRODUCT_DEFAULT_VALUES,
  });

  // Stable ref so the loadProductData effect doesn't re-run on every form render
  const methodsRef = useRef(methods);
  methodsRef.current = methods;

  useEffect(() => {
    if (!productId || !productDetailsData) return;

    const loadProductData = async () => {
      try {
        const productData = productDetailsData?.payload as SellerProductFullDetails | undefined;
        let attributes: CategoryAttribute[] = [];
        if (productData?.categoryId) {
          try {
            const attrRes = await getCategoryAttributes({ categoryId: productData.categoryId }).unwrap();
            attributes = attrRes?.payload || [];
          } catch (attrErr) {
            console.error('Failed to fetch category attributes for variant mapping', attrErr);
          }
        }

        if (productData) {
          methodsRef.current.reset(mapProductToFormValues(productData, attributes, false, exchangeRate) as AddProductSchema);
        }
      } catch {
        message.error(t('failedToLoadProduct'));
      }
    };

    loadProductData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, productDetailsData]);

  // Unsaved changes warning
  const { isDirty } = methods.formState;
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const handleSubmit = async (data: AddProductSchema) => {
    try {
      const payload = buildAddProductPayload(data);
      if (editProductId) {
        await updateProductApi({ productId: editProductId, body: payload }).unwrap();
        message.success(t('productUpdatedSuccess'));
      } else {
        await createProductApi({ sellerId: sellerId || '', body: payload }).unwrap();
        message.success(t('productCreatedSuccess'));
      }

      if (sellerId) {
        navigate(`/sellers/${sellerId}/profile`, { state: { activeTab: 'products' } });
      } else {
        navigate('/products');
      }
      methods.reset();
    } catch (err) {
      handleBackendErrors(err, methods.setError, ADD_PRODUCT_FIELD_MAPPING);
    }
  };

  return (
    <AddProductLookupsProvider>
      <FormProvider {...methods}>
        <div
          className="flex flex-col w-full items-start"
          data-testid="add-product-content"
        >
          <div
            className="w-full"
            data-testid="add-product-form-wrapper"
          >
            {isLoadingFullDetails ? (
              <div className="flex flex-col gap-6">
                <Skeleton active paragraph={{ rows: 6 }} className="bg-white dark:bg-dark-surface-primary p-6 rounded-2xl border border-light-input-border dark:border-dark-input-border" />
                <Skeleton active paragraph={{ rows: 6 }} className="bg-white dark:bg-dark-surface-primary p-6 rounded-2xl border border-light-input-border dark:border-dark-input-border" />
                <Skeleton active paragraph={{ rows: 6 }} className="bg-white dark:bg-dark-surface-primary p-6 rounded-2xl border border-light-input-border dark:border-dark-input-border" />
              </div>
            ) : (
              <form
                id="add-product-form"
                data-testid="add-product-form"
                onSubmit={methods.handleSubmit(handleSubmit, (errors) => {
                  const firstError = getFirstErrorMsg(errors as unknown as Record<string, unknown>);
                  message.error(firstError || t('Validation failed'));
                })}
              >
                <ScrollToError />
                <fieldset disabled={isSubmitting} className="contents group">
                  <ProductInformationForm />
                  <PricingTradeForm />
                  <SpecificationsForm />
                  <SkuInventoryForm />
                  <CustomizationForm />
                </fieldset>
              </form>
            )}
          </div>
        </div>
      </FormProvider>
    </AddProductLookupsProvider>
  );
};

export default AddProductContent;
